<template>
  <div class="reporte-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="reporte-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-icon-box">
          <v-icon color="#F2F2ED" size="20">mdi-cash-multiple</v-icon>
        </div>
        <div>
          <h1 class="page-title">Reporte de pagos</h1>
          <p class="page-sub">Análisis completo de pagos y transacciones</p>
        </div>
      </header>

      <!-- Filtros de período -->
      <section class="fade-in delay-100">
        <div class="periodo-wrapper">
          <periodo-busqueda @buscar="onBuscar" />
        </div>
      </section>

      <!-- Totales por membresías -->
      <section v-if="pagos.length > 0" class="fade-in delay-150 mt-lg">
        <div class="totales-wrapper">
          <cartas-totales
            :totales="totalesMembresias"
            :titulo="'Pagos realizados membresías'"
            :icono="'mdi-cash-multiple'"
            :color="'secondary'"
          />
        </div>
      </section>

      <!-- Totales por clases -->
      <section v-if="pagosClases.length > 0" class="fade-in delay-200 mt-lg">
        <div class="totales-wrapper">
          <cartas-totales
            :totales="totalesClases"
            :titulo="'Pagos de clases'"
            :icono="'mdi-dumbbell'"
            :color="'green'"
          />
        </div>
      </section>

      <!-- Card principal -->
      <section class="fade-in delay-250 mt-lg">
        <div class="card-flat card-flat-nopad">
          <!-- Resumen -->
          <div class="summary-head">
            <div>
              <span class="summary-label">Pagos totales</span>
              <div class="summary-value">${{ formatMoney(totalPagos) }}</div>
              <div class="summary-breakdown">
                <span class="breakdown-chip">
                  <v-icon size="13" class="mr-1"
                    >mdi-account-card-details</v-icon
                  >
                  Membresías
                  <strong>${{ formatMoney(totalMembresias) }}</strong>
                </span>
                <span class="breakdown-chip">
                  <v-icon size="13" class="mr-1">mdi-dumbbell</v-icon>
                  Clases <strong>${{ formatMoney(totalClases) }}</strong>
                </span>
              </div>
            </div>
            <div class="summary-period">
              <v-icon size="14" class="mr-2" color="#5C5C62"
                >mdi-calendar-range</v-icon
              >
              <span v-if="!filtros.fechaInicio">Hoy</span>
              <span v-else
                >{{ filtros.fechaInicio }} – {{ filtros.fechaFin }}</span
              >
            </div>
          </div>

          <!-- Controles -->
          <div class="table-controls">
            <div class="search-input-wrapper">
              <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
              <input
                v-model="busqueda"
                type="text"
                class="search-input"
                placeholder="Buscar en la tabla…"
              />
            </div>

            <div class="export-buttons">
              <button class="btn-ghost" @click="generarPDF">
                <v-icon size="15" class="mr-2">mdi-file-pdf-box</v-icon>
                PDF
              </button>
              <button class="btn-primary" @click="generarExcel">
                <v-icon size="15" class="mr-2">mdi-file-excel</v-icon>
                Excel
              </button>
            </div>
          </div>

          <!-- Tabla -->
          <v-data-table
            :loading="cargando"
            :headers="encabezadoTabla"
            :items="pagos"
            :search="busqueda"
            sort-by="nombre"
            no-data-text="No hay pagos registrados"
            no-results-text="No se encontraron coincidencias"
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.imagen`]="{ item }">
              <img
                v-if="item.imagen"
                :src="urlImagen(item.imagen)"
                alt="Miembro"
                class="member-image"
              />
              <div v-else class="member-image-placeholder">
                <v-icon size="14" color="#5C5C62">mdi-account</v-icon>
              </div>
            </template>

            <template v-slot:[`item.nombre`]="{ item }">
              <span class="cell-strong">{{ item.nombre }}</span>
            </template>

            <template v-slot:[`item.matricula`]="{ item }">
              <span class="status-tag status-tag-neutral">{{
                item.matricula
              }}</span>
            </template>

            <template v-slot:[`item.tipo`]="{ item }">
              <span class="status-tag" :class="claseTipoPago(item.tipo)">
                <v-icon size="11" class="mr-1">{{
                  iconoTipoPago(item.tipo)
                }}</v-icon>
                {{ textoTipoPago(item.tipo) }}
              </span>
            </template>

            <template v-slot:[`item.membresia`]="{ item }">
              <span
                v-if="item.tipo === 'membresia'"
                class="membership-chip"
                :class="claseMembresia(item.membresia)"
              >
                <v-icon size="12" class="mr-1">{{
                  iconoMembresia(item.membresia)
                }}</v-icon>
                {{ item.membresia }}
              </span>
              <span
                v-else-if="item.tipo === 'clase' && item.nombre_clase"
                class="membership-chip chip-accent"
              >
                <v-icon size="12" class="mr-1" color="#C8FF4D"
                  >mdi-dumbbell</v-icon
                >
                {{ item.nombre_clase }}
              </span>
              <span
                v-else-if="item.tipo === 'inscripcion'"
                class="membership-chip chip-neutral"
              >
                <v-icon size="12" class="mr-1" color="#FFB84D"
                  >mdi-cash-plus</v-icon
                >
                Inscripción
              </span>
              <span v-else class="cell-muted">—</span>
            </template>

            <template v-slot:[`item.fecha`]="{ item }">
              <span class="cell-secondary">{{
                fechaFormateada(item.fecha)
              }}</span>
            </template>

            <template v-slot:[`item.monto`]="{ item }">
              <span class="cell-price">${{ formatMoney(item.monto) }}</span>
            </template>

            <template v-slot:[`item.usuario`]="{ item }">
              <div class="flex items-center gap-xs">
                <v-icon size="12" color="#5C5C62">mdi-account-tie</v-icon>
                <span class="cell-secondary">{{ item.usuario }}</span>
              </div>
            </template>

            <template slot="no-data">
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-cash-remove</v-icon>
                </div>
                <h3 class="empty-title">No hay pagos registrados</h3>
                <p class="empty-desc">
                  No se encontraron pagos para el período seleccionado
                </p>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando datos…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>

      <!-- Totales por usuario -->
      <section v-if="pagos.length > 0" class="fade-in delay-300 mt-lg">
        <div class="totales-wrapper">
          <cartas-totales
            :totales="totalesUsuarios"
            :titulo="'Pagos realizados por usuario'"
            :icono="'mdi-account-cash'"
            :color="'green darken-3'"
          />
        </div>
      </section>

      <!-- Totales por miembros -->
      <section v-if="pagos.length > 0" class="fade-in delay-350 mt-lg">
        <div class="totales-wrapper">
          <cartas-totales-miembros
            :totales="totalesMiembros"
            :titulo="'Pagos realizados por miembros'"
          />
        </div>
      </section>
    </div>

    <!-- Loading Overlay -->
    <div v-if="cargando && pagos.length === 0" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando reporte de pagos…</p>
      </div>
    </div>
  </div>
</template>

<script>
import HttpService from "../../Servicios/HttpService";
import Utiles from "../../Servicios/Utiles";
import PeriodoBusqueda from "../Dialogos/PeriodoBusqueda.vue";
import CartasTotales from "../Dialogos/CartasTotales.vue";
import CartasTotalesMiembros from "../Dialogos/CartasTotalesMiembros.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatearFechaHora } from "@/utils/fechas";

export default {
  name: "Pagos",
  components: { PeriodoBusqueda, CartasTotales, CartasTotalesMiembros },

  data: () => ({
    busqueda: "",
    cargando: false,
    pagos: [],
    encabezadoTabla: [
      { text: "Fotografía", sortable: true, value: "imagen" },
      { text: "Miembro", sortable: true, value: "nombre" },
      { text: "Matrícula", sortable: true, value: "matricula" },
      { text: "Tipo", sortable: true, value: "tipo" },
      { text: "Membresía/Clase", sortable: true, value: "membresia" },
      { text: "Fecha", sortable: true, value: "fecha" },
      { text: "Monto pagado", sortable: true, value: "monto" },
      { text: "Cobró", sortable: true, value: "usuario" }
    ],
    filtros: {
      fechaInicio: null,
      fechaFin: null
    },
    totalPagos: 0,
    totalesMembresias: [],
    totalesClases: [],
    totalesUsuarios: [],
    totalesMiembros: []
  }),

  computed: {
    pagosMembresias() {
      return this.pagos.filter(pago => pago.tipo === "membresia");
    },
    pagosClases() {
      return this.pagos.filter(pago => pago.tipo === "clase");
    },
    totalMembresias() {
      return this.pagosMembresias.reduce(
        (sum, pago) => sum + parseFloat(pago.monto || 0),
        0
      );
    },
    totalClases() {
      return this.pagosClases.reduce(
        (sum, pago) => sum + parseFloat(pago.monto || 0),
        0
      );
    }
  },

  mounted() {
    this.obtenerPagos();
  },

  methods: {
    formatMoney(amount) {
      if (!amount) return "0.00";
      return parseFloat(amount).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    colorTipoPago(tipo) {
      const colores = {
        membresia: "blue",
        clase: "green",
        inscripcion: "orange",
        default: "grey"
      };
      return colores[tipo] || colores.default;
    },

    iconoTipoPago(tipo) {
      const iconos = {
        membresia: "mdi-badge-account",
        clase: "mdi-dumbbell",
        inscripcion: "mdi-cash-plus",
        default: "mdi-cash"
      };
      return iconos[tipo] || iconos.default;
    },

    textoTipoPago(tipo) {
      const textos = {
        membresia: "Membresía",
        clase: "Clase",
        inscripcion: "Inscripción",
        default: "Pago"
      };
      return textos[tipo] || textos.default;
    },

    claseTipoPago(tipo) {
      if (tipo === "membresia") return "status-tag-info";
      if (tipo === "inscripcion") return "status-tag-warning";
      return "status-tag-success"; // clase
    },

    fechaFormateada(fecha) {
      return formatearFechaHora(fecha);
    },

    generarExcel() {
      try {
        const datos = this.pagos.map(pago => ({
          Miembro: pago.nombre,
          Matrícula: pago.matricula,
          Tipo: this.textoTipoPago(pago.tipo),
          "Membresía/Clase":
            pago.tipo === "membresia"
              ? pago.membresia
              : pago.nombre_clase || "Clase",
          "Detalle Clase":
            pago.tipo === "clase" ? `${pago.nombre_instructor || ""}` : "-",
          Fecha: pago.fecha,
          "Monto pagado": pago.monto,
          Usuario: pago.usuario
        }));

        const ws = XLSX.utils.json_to_sheet(datos, { origin: "A3" });
        XLSX.utils.sheet_add_aoa(ws, [["Reporte de Pagos"], []], {
          origin: "A1"
        });

        ws["!cols"] = [
          { wch: 20 },
          { wch: 15 },
          { wch: 12 },
          { wch: 20 },
          { wch: 20 },
          { wch: 15 },
          { wch: 15 },
          { wch: 20 }
        ];
        ws["!autofilter"] = { ref: "A3:H3" };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Pagos");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([wbout], { type: "application/octet-stream" }),
          "reporte_pagos.xlsx"
        );

        this.$store.commit("mostrarMensaje", {
          texto: "✅ Excel generado correctamente",
          color: "success"
        });
      } catch (error) {
        console.error("Error generando Excel:", error);
        this.$store.commit("mostrarMensaje", {
          texto: "❌ Error al generar el Excel",
          color: "error"
        });
      }
    },

    generarPDF() {
      try {
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "pt",
          format: "A4"
        });
        const pageWidth = doc.internal.pageSize.getWidth();

        const title = "Reporte de Pagos";
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        const textWidth = doc.getTextWidth(title);
        doc.text(title, (pageWidth - textWidth) / 2, 40);

        // Subtítulo con período
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        let subtitle = "Período: ";
        if (this.filtros.fechaInicio) {
          subtitle += `${this.filtros.fechaInicio} - ${this.filtros.fechaFin}`;
        } else {
          subtitle += "Hoy";
        }
        const subtitleWidth = doc.getTextWidth(subtitle);
        doc.text(subtitle, (pageWidth - subtitleWidth) / 2, 60);

        const columnas = [
          { header: "Miembro", dataKey: "nombre" },
          { header: "Matrícula", dataKey: "matricula" },
          { header: "Tipo", dataKey: "tipo" },
          { header: "Membresía/Clase", dataKey: "membresia_clase" },
          { header: "Fecha", dataKey: "fecha" },
          { header: "Monto", dataKey: "monto" },
          { header: "Cobró", dataKey: "usuario" }
        ];

        autoTable(doc, {
          startY: 80,
          head: [columnas.map(col => col.header)],
          body: this.pagos.map(item => [
            item.nombre,
            item.matricula,
            this.textoTipoPago(item.tipo),
            item.tipo === "membresia"
              ? item.membresia
              : item.nombre_clase || "Clase",
            item.fecha,
            `$${this.formatMoney(item.monto)}`,
            item.usuario
          ]),
          theme: "striped",
          headStyles: {
            fillColor: [200, 255, 77],
            textColor: [14, 14, 16],
            fontStyle: "bold"
          },
          styles: {
            fontSize: 9,
            cellPadding: 4
          },
          margin: { top: 80, bottom: 50 }
        });

        // Total al final
        const finalY = doc.lastAutoTable.finalY + 20;
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(
          `Total de Pagos: $${this.formatMoney(this.totalPagos)}`,
          40,
          finalY
        );

        doc.save("reporte_pagos.pdf");

        this.$store.commit("mostrarMensaje", {
          texto: "✅ PDF generado correctamente",
          color: "success"
        });
      } catch (error) {
        console.error("Error generando PDF:", error);
        this.$store.commit("mostrarMensaje", {
          texto: "❌ Error al generar el PDF",
          color: "error"
        });
      }
    },

    onBuscar(fechas) {
      console.log(fechas);
      this.filtros = {
        fechaInicio: fechas[0],
        fechaFin: fechas[1]
      };
      this.obtenerPagos();
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    async obtenerPagos() {
      this.cargando = true;
      let payload = {
        metodo: "obtener",
        filtros: this.filtros
      };

      try {
        const respuesta = await HttpService.obtenerConDatos(
          payload,
          "pagos.php"
        );

        this.pagos = respuesta.pagos;
        this.totalPagos = respuesta.totalPagos;
        this.totalesMembresias = respuesta.totalesMembresias.map(item => ({
          ...item,
          total: parseFloat(item.total) || 0
        }));

        this.totalesClases = respuesta.totalesClases.map(item => ({
          ...item,
          total: parseFloat(item.total) || 0
        }));

        this.totalesUsuarios = respuesta.totalesUsuario;
        this.totalesMiembros = respuesta.totalesMiembros;

        console.log("Totales convertidos:", {
          membresias: this.totalesMembresias,
          clases: this.totalesClases
        });
      } catch (error) {
        console.error("Error cargando pagos:", error);
        this.$store.commit("mostrarMensaje", {
          texto: "❌ Error al cargar los pagos",
          color: "error"
        });
      } finally {
        this.cargando = false;
      }
    },

    claseMembresia(nombre) {
      const tipo = (nombre && nombre.toLowerCase()) || "";
      if (tipo.includes("semanal") || tipo.includes("semestral"))
        return "chip-accent";
      return "chip-neutral";
    },

    iconoMembresia(nombre) {
      const tipo = (nombre && nombre.toLowerCase()) || "";
      if (tipo.includes("semanal")) return "mdi-trophy";
      if (tipo.includes("mensual hombre")) return "mdi-face-man";
      if (tipo.includes("mensual mujer")) return "mdi-face-woman";
      if (tipo.includes("mensual pareja") || tipo.includes("estudiante"))
        return "mdi-human-male-female";
      if (tipo.includes("semestral")) return "mdi-calendar-range";
      return "mdi-certificate";
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap");

/* ===== TOKENS ===== */
.reporte-page {
  --bg: #0e0e10;
  --card: #17171a;
  --card-border: #26262a;
  --text-primary: #f2f2ed;
  --text-secondary: #8c8c93;
  --text-tertiary: #5c5c62;
  --accent: #c8ff4d;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --radius: 4px;

  --font-display: "Space Grotesk", sans-serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", monospace;
}

.reporte-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image: linear-gradient(
      rgba(242, 242, 237, 0.02) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.reporte-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.periodo-wrapper,
.totales-wrapper {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px 22px;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

/* ===== SUMMARY ===== */
.summary-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid var(--card-border);
}

.summary-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.summary-value {
  font-family: var(--font-display);
  font-size: 32px;
  font-weight: 700;
  color: var(--accent);
  margin: 4px 0 12px;
}

.summary-breakdown {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.breakdown-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  font-size: 12px;
  color: var(--text-secondary);
}

.breakdown-chip strong {
  color: var(--text-primary);
  margin-left: 4px;
}

.summary-period {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
  flex-shrink: 0;
}

/* ===== CONTROLS ===== */
.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 18px 24px;
  border-bottom: 1px solid var(--card-border);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  flex: 1;
  min-width: 240px;
  max-width: 420px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.export-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

/* ===== TABLE CELLS ===== */
.member-image {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
}

.member-image-placeholder {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.cell-muted {
  font-size: 13px;
  color: var(--text-tertiary);
}

.cell-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  white-space: nowrap;
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

.status-tag-info {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.membership-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--radius);
  font-size: 11px;
  font-weight: 600;
}

.membership-chip.chip-accent {
  background: var(--accent-dim);
  color: var(--accent);
}

.membership-chip.chip-neutral {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-secondary);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 {
  animation-delay: 0.05s;
}
.delay-150 {
  animation-delay: 0.07s;
}
.delay-200 {
  animation-delay: 0.1s;
}
.delay-250 {
  animation-delay: 0.12s;
}
.delay-300 {
  animation-delay: 0.15s;
}
.delay-350 {
  animation-delay: 0.17s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in,
  .spinner {
    animation: none;
  }
}

/* ===== Overrides v-data-table (Vuetify) ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 24px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 24px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 24px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

.dash-table >>> .v-data-table__progress {
  background: var(--accent) !important;
}

.status-tag-warning {
  background: rgba(255, 184, 77, 0.14);
  color: #ffb84d;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .reporte-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .summary-value {
    font-size: 24px;
  }

  .table-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-wrapper {
    max-width: none;
  }

  .export-buttons .btn-primary,
  .export-buttons .btn-ghost {
    flex: 1;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Pagos/Pagos.vue